import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../context/AuthContext';
import CategoryPicker from './CategoryPicker';
import FiltersForm from './FiltersForm';
import ResultsView from './ResultsView';
import {
  MARKETPLACES, SCAN_LIMITS, TABS, TARGET_COUNTS, buildFilterPayload,
} from './constants';
import {
  estimateTokens, getSearch, getStatus, listSearches, startSearch, updateLandedCost,
} from './productFinderApi';
import styles from './ProductFinder.module.css';

const POLL_MS = 2000;
const EMPTY_TABS = { active: null, oos: null };

// Product Finder (Keepa): pick categories and filters, run a search on the server
// (a background job with progress), then work the results. Free while in testing.
const ProductFinderPage = () => {
  const { token } = useContext(AuthContext);

  const [status, setStatus] = useState(null);
  const [domain, setDomain] = useState(10);
  const [targetCount, setTargetCount] = useState(200);
  const [scanLimit, setScanLimit] = useState(300);
  const [landedPct, setLandedPct] = useState(30);
  const [deepData, setDeepData] = useState(false);
  const [categoriesByDomain, setCategoriesByDomain] = useState({});
  const [tab, setTab] = useState('active');
  const [filters, setFilters] = useState({ active: {}, oos: {} });
  const [estimate, setEstimate] = useState(null);
  const [searches, setSearches] = useState(EMPTY_TABS); // latest search view per tab
  const [errors, setErrors] = useState(EMPTY_TABS);
  const [submitting, setSubmitting] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recent, setRecent] = useState([]);

  const market = MARKETPLACES.find((m) => m.domain === domain) || MARKETPLACES[0];
  const categories = useMemo(() => categoriesByDomain[domain] || [], [categoriesByDomain, domain]);
  const current = searches[tab];
  const running = current && (current.status === 'queued' || current.status === 'running');

  const refreshStatus = useCallback(() => {
    if (!token) return;
    getStatus(token).then(setStatus).catch(() => {});
  }, [token]);

  const refreshRecent = useCallback(() => {
    if (!token) return;
    listSearches(token).then(setRecent).catch(() => {});
  }, [token]);

  // On load: token status, and the latest search of each tab (still running or finished)
  useEffect(() => {
    if (!token) return;
    refreshStatus();
    listSearches(token).then(async (list) => {
      setRecent(list);
      for (const t of ['active', 'oos']) {
        const latest = list.find((s) => s.tab === t);
        if (latest) {
          try {
            const view = await getSearch(token, latest.id);
            setSearches((prev) => ({ ...prev, [t]: view }));
          } catch (e) { /* ignore */ }
        }
      }
    }).catch(() => {});
  }, [token, refreshStatus]);

  // Poll whichever tabs have a search in flight
  const searchesRef = useRef(searches);
  searchesRef.current = searches;
  const inFlight = ['active', 'oos']
    .filter((t) => searches[t] && ['queued', 'running'].includes(searches[t].status))
    .map((t) => `${t}:${searches[t].id}`)
    .join(',');
  useEffect(() => {
    if (!inFlight || !token) return undefined;
    const timer = setInterval(async () => {
      for (const t of ['active', 'oos']) {
        const s = searchesRef.current[t];
        if (!s || !['queued', 'running'].includes(s.status)) continue;
        try {
          const view = await getSearch(token, s.id);
          setSearches((prev) => ({ ...prev, [t]: view }));
          if (view.status === 'done' || view.status === 'failed') {
            refreshStatus();
            refreshRecent();
          }
        } catch (e) { /* keep polling */ }
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [inFlight, token, refreshStatus, refreshRecent]);

  const requestBody = useCallback((t) => ({
    tab: t,
    domain,
    target_count: targetCount,
    scan_limit: Math.max(scanLimit, targetCount),
    deep_data: deepData,
    landed_cost_pct: landedPct,
    filters: buildFilterPayload(filters[t], t, deepData, categories),
  }), [domain, targetCount, scanLimit, deepData, landedPct, filters, categories]);

  // Token estimate (depends on categories, identifiers and result size only)
  const identifiers = filters[tab].identifiers || '';
  useEffect(() => {
    if (!token) return undefined;
    const timer = setTimeout(() => {
      const body = requestBody(tab);
      estimateTokens(token, { ...body, filters: { categories: body.filters.categories, identifiers: body.filters.identifiers } })
        .then(setEstimate)
        .catch(() => setEstimate(null));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab, domain, targetCount, scanLimit, deepData, categories, identifiers]);

  const runSearch = async () => {
    setErrors((e) => ({ ...e, [tab]: null }));
    setSubmitting(true);
    try {
      const { id } = await startSearch(token, requestBody(tab));
      setSearches((prev) => ({ ...prev, [tab]: { id, tab, status: 'queued', progress: { message: 'Queued…', fraction: 0 } } }));
    } catch (e) {
      if (e.status === 409 && e.body && e.body.id) {
        // A search is already running: show its progress instead
        try {
          const view = await getSearch(token, e.body.id);
          setSearches((prev) => ({ ...prev, [view.tab]: view }));
          setTab(view.tab);
        } catch (err) { /* ignore */ }
      }
      setErrors((x) => ({ ...x, [tab]: e.message }));
    } finally {
      setSubmitting(false);
    }
  };

  // Landed cost changes recompute margins and scores on the server (no Keepa tokens)
  const applyLandedCost = useCallback(async (pct, overrides) => {
    const s = searchesRef.current[tab];
    if (!s || s.status !== 'done') return;
    setRecomputing(true);
    try {
      const view = await updateLandedCost(token, s.id, pct ?? s.landed_cost_pct, overrides ?? s.landed_overrides);
      setSearches((prev) => ({ ...prev, [tab]: view }));
    } catch (e) {
      setErrors((x) => ({ ...x, [tab]: e.message }));
    } finally {
      setRecomputing(false);
    }
  }, [token, tab]);

  // Default landed cost % follows the slider for the search on screen
  useEffect(() => {
    if (!current || current.status !== 'done' || current.landed_cost_pct === landedPct) return undefined;
    const timer = setTimeout(() => applyLandedCost(landedPct, undefined), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landedPct, current && current.id]);

  const openRecent = async (id) => {
    if (!id) return;
    try {
      const view = await getSearch(token, id);
      setSearches((prev) => ({ ...prev, [view.tab]: view }));
      setTab(view.tab);
      if (view.landed_cost_pct) setLandedPct(view.landed_cost_pct);
    } catch (e) {
      setErrors((x) => ({ ...x, [tab]: e.message }));
    }
  };

  const tabLabel = TABS.find((t) => t.key === tab).label;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Product Finder</h1>
          <p className={styles.subtitle}>
            Marketplace: <b>{market.label}</b> · leave any filter blank for no limit
          </p>
        </div>
        <div className={styles.status}>
          {status && status.demo_mode && <span className={styles.demoBadge}>Demo mode (no Keepa key)</span>}
          {status && status.tokens_left !== null && status.tokens_left !== undefined && (
            <span>
              Keepa tokens left: <b>{status.tokens_left}</b>
              {status.refill_rate ? ` · refills ${status.refill_rate}/min` : ''}
            </span>
          )}
          <span className={styles.freeBadge}>Free while in testing</span>
        </div>
      </header>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Search settings</h3>
        <div className={styles.settingsGrid}>
          <label className={styles.field}>
            <span className={styles.label}>Amazon Marketplace</span>
            <select className={styles.input} value={domain} onChange={(e) => setDomain(Number(e.target.value))}>
              {MARKETPLACES.map((m) => <option key={m.domain} value={m.domain}>{m.label}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Products to return</span>
            <select className={styles.input} value={targetCount} onChange={(e) => setTargetCount(Number(e.target.value))}>
              {TARGET_COUNTS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className={styles.field} title="Products are fetched 100 at a time until enough pass your filters. Each fetched product costs about 2 tokens.">
            <span className={styles.label}>Most products to scan ⓘ</span>
            <select className={styles.input} value={scanLimit} onChange={(e) => setScanLimit(Number(e.target.value))}>
              {SCAN_LIMITS.filter((n) => n >= targetCount).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className={styles.field} title="Product cost + freight + duty + packaging + inbound to Amazon, per unit. Used for every product unless you type an exact amount for it. Changing this costs no tokens.">
            <span className={styles.label}>Default landed cost: {landedPct}% of price ⓘ</span>
            <input type="range" min="5" max="80" step="5" value={landedPct} onChange={(e) => setLandedPct(Number(e.target.value))} />
          </label>
          <label className={styles.switchField} title="Adds the buy box seller (name, country) and parent-level sales by fetching the sibling variations.">
            <input type="checkbox" checked={deepData} onChange={(e) => setDeepData(e.target.checked)} />
            <span>
              Deep data (seller &amp; parent-level)
              {deepData && <small className={styles.warnInline}> Roughly doubles or triples the tokens per search.</small>}
            </span>
          </label>
        </div>
        {recent.length > 0 && (
          <label className={styles.field}>
            <span className={styles.label}>Recent searches</span>
            <select className={styles.input} value="" onChange={(e) => openRecent(e.target.value)}>
              <option value="">Open a recent search…</option>
              {recent.map((r) => (
                <option key={r.id} value={r.id}>
                  {`${new Date(r.created_at).toLocaleString()} · ${r.tab === 'active' ? 'Active' : 'Out of stock'} · `
                    + `${r.categories.length ? r.categories.join(', ') : 'All categories'} · `
                    + `${r.status === 'done' ? `${r.returned_count} products` : r.status}`}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      <CategoryPicker
        token={token}
        domain={domain}
        selected={categories}
        onChange={(list) => setCategoriesByDomain((prev) => ({ ...prev, [domain]: list }))}
      />

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={t.key === tab ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setTab(t.key)}
          >
            {t.key === 'active' ? '🔥 ' : '📦 '}{t.label}
          </button>
        ))}
      </div>

      <section className={styles.card}>
        <FiltersForm
          tab={tab}
          values={filters[tab]}
          onChange={(v) => setFilters((prev) => ({ ...prev, [tab]: v }))}
          deepData={deepData}
          market={market}
        />
        <p className={styles.caption}>
          Returns up to <b>{targetCount}</b> products, scanning at most <b>{Math.max(scanLimit, targetCount)}</b>.
          {estimate && <> Estimated cost: <b>{estimate.low}–{estimate.high} tokens</b> (repeats within 24 h come from the cache).</>}
        </p>
        <button type="button" className={styles.primaryBtn} onClick={runSearch} disabled={submitting || running || !token}>
          🔍 Search · {tabLabel}
        </button>
        {errors[tab] && <p className={styles.error}>{errors[tab]}</p>}
        {running && (
          <div className={styles.progressWrap}>
            <div className={styles.progressBar}>
              <div style={{ width: `${Math.round((current.progress?.fraction || 0) * 100)}%` }} />
            </div>
            <span className={styles.caption}>{current.progress?.message || 'Working…'}</span>
          </div>
        )}
        {current && current.status === 'failed' && <p className={styles.error}>{current.error || 'Search failed.'}</p>}
      </section>

      <section className={styles.card}>
        {!current && <p className={styles.info}>Set your filters and press Search. Nothing is fetched until you do.</p>}
        {current && current.status === 'done' && (
          <ResultsView search={current} onLandedCost={applyLandedCost} recomputing={recomputing} />
        )}
        {running && <p className={styles.info}>Results appear here when the search finishes. You can leave this page; it keeps running.</p>}
      </section>
    </div>
  );
};

export default ProductFinderPage;
